/**
 * Godot's triplanar mapping in three's programs: a UV set that projects samples each of its maps at
 * the vertex position, three times, and blends the samples by the normal (`material.cpp:1386-1500`).
 * The surface's tangent frame follows the projection, as Godot writes TANGENT and BINORMAL. The
 * colour programs and the depth programs of the surface's shadow read the same record.
 */
import { editedProgram, rewrittenShader, rewrittenThroughChunks } from '../shaderPatch/chunkPatch';
import { recordedOn } from './recordedOnMaterial';
import { perObject } from '../../utils/perObject';
import { DRAWN_INSTANCE, DRAWN_MODEL, NODE_POSE_PARS, nodePoseUniforms } from './nodePose';
/** Where a material records its triplanar mapping. */
const TRIPLANAR_KEY = 'godotTriplanar';
/** The `userData` that gives a material its triplanar mapping, none for `null`. */
export function triplanarUserData(record) {
    return record ? { [TRIPLANAR_KEY]: record } : {};
}
/** The UV sets of Godot's shader, by the prefix of their uniforms. */
const UV_SETS = ['uv1', 'uv2'];
/** `text` with its first letter in upper case, as GLSL names join words. */
function capitalised(text) {
    return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}
/** The GLSL name of UV set `uv`'s `name`, as `godotUv1TriplanarScale`. */
function glslName(uv, name) {
    return `godot${capitalised(uv)}${name}`;
}
/** The varying of UV set `uv`'s `name`, as `vGodotUv1TriplanarPos`. */
function varyingName(uv, name) {
    return `v${capitalised(glslName(uv, name))}`;
}
/** UV set `uv`'s projected position and blend weights, as each stage names the varyings. */
function projectionVaryings(uv) {
    return { position: varyingName(uv, 'TriplanarPos'), weights: varyingName(uv, 'PowerNormal') };
}
/** UV set `uv`'s define, which each program state of the record turns on. */
const projects = (uv) => `GODOT_${uv.toUpperCase()}_TRIPLANAR`;
const projectsWorld = (uv) => `GODOT_${uv.toUpperCase()}_WORLD_TRIPLANAR`;
/** A projection's uniforms, the first value until a material records one. */
const NO_PROJECTION = {
    scale: { x: 1, y: 1, z: 1 },
    offset: { x: 0, y: 0, z: 0 },
    sharpness: 1,
    isWorld: false,
};
/** Uniforms that read `material`'s record at each upload, so a re-parse that changes only a value needs no recompile. */
function projectionUniforms(material, uv) {
    const projection = () => recordedOn(material, TRIPLANAR_KEY, null)?.[uv] ?? NO_PROJECTION;
    // three uploads a vec3 from any object with x, y and z, so the record's own vectors serve.
    return {
        [glslName(uv, 'TriplanarScale')]: {
            get value() {
                return projection().scale;
            },
        },
        [glslName(uv, 'TriplanarOffset')]: {
            get value() {
                return projection().offset;
            },
        },
        [glslName(uv, 'TriplanarSharpness')]: {
            get value() {
                return projection().sharpness;
            },
        },
    };
}
/** One UV set's varyings, in both stages. */
function projectionVaryingPars(uv) {
    const { position, weights } = projectionVaryings(uv);
    return `#ifdef ${projects(uv)}
varying vec3 ${position};
varying vec3 ${weights};
#endif
`;
}
/** One UV set's uniforms and varyings, in the vertex program. */
function projectionVertexPars(uv) {
    return `#ifdef ${projects(uv)}
uniform vec3 ${glslName(uv, 'TriplanarScale')};
uniform vec3 ${glslName(uv, 'TriplanarOffset')};
uniform float ${glslName(uv, 'TriplanarSharpness')};
#endif
${projectionVaryingPars(uv)}`;
}
/**
 * The frame Godot writes for a triplanar surface, in view space. Each program declares it, so the
 * fragment programs that read no frame link too.
 */
const FRAME_VARYINGS = `varying vec3 vGodotTriplanarTangent;
varying vec3 vGodotTriplanarBinormal;
`;
/**
 * MODEL_NORMAL_MATRIX up to a positive scale, which every reader normalises away. Godot inverts per
 * vertex under a non-uniform scale (`scene_forward_clustered.glsl:256-260`). The cofactors give the
 * same turn for three cross products.
 */
const NORMAL_MATRIX = /* glsl */ `mat3 godotNormalMatrix( const in mat3 m ) {
	mat3 cofactors = mat3( cross( m[ 1 ], m[ 2 ] ), cross( m[ 2 ], m[ 0 ] ), cross( m[ 0 ], m[ 1 ] ) );
	return cofactors * sign( dot( m[ 0 ], cofactors[ 0 ] ) );
}
`;
const VERTEX_PARS = `${NODE_POSE_PARS}${UV_SETS.map(projectionVertexPars).join('')}${FRAME_VARYINGS}${NORMAL_MATRIX}`;
/**
 * Godot's VERTEX and NORMAL: the mesh's, after skinning and morphs, before an instance's
 * transform. A scope of its own, since three runs the normal chunks in only some programs.
 * Godot's MODEL_MATRIX and MODEL_NORMAL_MATRIX are the node's, then the instance's, whose basis
 * turns the normal as it stands (`scene_forward_clustered.glsl:338-346`). Points at a GridMap's
 * cells or a CPUParticles3D's particles are instances too (`godotPoint.ts`).
 */
const GODOT_VERTEX_INPUTS = /* glsl */ `
	vec3 godotNormal;
	{
		#include <beginnormal_vertex>
		#include <morphnormal_vertex>
		#include <skinnormal_vertex>
		godotNormal = objectNormal;
	}
	vec3 godotVertex = transformed;
	mat4 godotModel = ${DRAWN_MODEL} * godotModelFromObject;
	mat3 godotModelNormal = godotNormalMatrix( mat3( godotModel ) );
	#ifdef USE_INSTANCING
		godotModel *= ${DRAWN_INSTANCE};
		godotModelNormal *= mat3( ${DRAWN_INSTANCE} );
	#endif
`;
/**
 * Godot's TANGENT and BINORMAL for a triplanar surface (`material.cpp:1386-1414`), carried to view
 * space as Godot's `modelview_normal` carries them (`scene_forward_clustered.glsl:461-470`). A
 * billboard's is the model-view its pose writes for a plain and an instanced draw alike, which
 * `modelViewMatrix` reads after the pose (`surfaceBillboard.ts`).
 */
const GODOT_FRAME = /* glsl */ `
	#ifdef GODOT_TRIPLANAR_TANGENT_WORLD
		vec3 godotFrameNormal = godotModelNormal * godotNormal;
	#else
		vec3 godotFrameNormal = godotNormal;
	#endif
	vec3 godotTangent = vec3( 0.0, 0.0, - 1.0 ) * abs( godotFrameNormal.x );
	godotTangent += vec3( 1.0, 0.0, 0.0 ) * abs( godotFrameNormal.y );
	godotTangent += vec3( 1.0, 0.0, 0.0 ) * abs( godotFrameNormal.z );
	vec3 godotBinormal = vec3( 0.0, 1.0, 0.0 ) * abs( godotFrameNormal.x );
	godotBinormal += vec3( 0.0, 0.0, - 1.0 ) * abs( godotFrameNormal.y );
	godotBinormal += vec3( 0.0, 1.0, 0.0 ) * abs( godotFrameNormal.z );
	#ifdef GODOT_TRIPLANAR_TANGENT_WORLD
		// Godot's own shader inverts per vertex here, material.cpp:1403.
		mat3 godotNormalModel = inverse( godotModelNormal );
		godotTangent = godotNormalModel * normalize( godotTangent );
		godotBinormal = godotNormalModel * normalize( godotBinormal );
	#else
		godotTangent = normalize( godotTangent );
		godotBinormal = normalize( godotBinormal );
	#endif
	#ifdef GODOT_TRIPLANAR_BILLBOARD
		mat3 godotModelViewNormal = mat3( modelViewMatrix );
	#else
		mat3 godotModelViewNormal = mat3( viewMatrix ) * godotModelNormal;
	#endif
	vGodotTriplanarTangent = normalize( godotModelViewNormal * godotTangent );
	vGodotTriplanarBinormal = normalize( godotModelViewNormal * godotBinormal );
`;
/**
 * UV set `uv`'s projected position and blend weights (`material.cpp:1418-1454`). UV1's world
 * weights read MODEL_NORMAL_MATRIX, UV2's read `mat3(MODEL_MATRIX)`, as Godot's do.
 */
function projectionVertex(uv) {
    const worldNormal = uv === 'uv1' ? 'godotModelNormal * godotNormal' : 'mat3( godotModel ) * godotNormal';
    const { position, weights } = projectionVaryings(uv);
    return /* glsl */ `
	#ifdef ${projects(uv)}
		#ifdef ${projectsWorld(uv)}
			vec3 ${glslName(uv, 'Normal')} = ${worldNormal};
			vec3 ${glslName(uv, 'Position')} = ( godotModel * vec4( godotVertex, 1.0 ) ).xyz;
		#else
			vec3 ${glslName(uv, 'Normal')} = godotNormal;
			vec3 ${glslName(uv, 'Position')} = godotVertex;
		#endif
		${weights} = pow( abs( ${glslName(uv, 'Normal')} ), vec3( ${glslName(uv, 'TriplanarSharpness')} ) );
		${weights} /= dot( ${weights}, vec3( 1.0 ) );
		${position} = ${glslName(uv, 'Position')} * ${glslName(uv, 'TriplanarScale')} + ${glslName(uv, 'TriplanarOffset')};
		${position} *= vec3( 1.0, - 1.0, 1.0 );
	#endif
`;
}
const PROJECT_VERTEX = '#include <project_vertex>';
const VERTEX = `${GODOT_VERTEX_INPUTS}${GODOT_FRAME}${UV_SETS.map(projectionVertex).join('')}${PROJECT_VERTEX}`;
/** three's coordinate of each map, `v<Map>Uv`. */
function coordinateOf(map) {
    return `v${capitalised(map)}Uv`;
}
const NORMAL_MAP_PARS = '#include <normalmap_pars_fragment>';
/**
 * The normal map's texel `step` pixels along the screen from this one, in UV1's three projections,
 * in place of the read `godotNormalMap.ts` defines. It follows three's declaration of `normalMap`.
 * The gradients come from `main`, where every pixel of a quad runs.
 */
const TRIPLANAR_NORMAL_TEXEL = /* glsl */ `${NORMAL_MAP_PARS}
#if defined( GODOT_UV1_TRIPLANAR ) && defined( USE_NORMALMAP_TANGENTSPACE )
uniform mat3 normalMapTransform;
vec4 godotTriplanarNormalTexel( const in vec3 position, const in vec3 weights, const in vec3 positionDx, const in vec3 positionDy ) {
	mat2 window = mat2( normalMapTransform );
	vec2 zy = vec2( - 1.0, 1.0 );
	return textureGrad( normalMap, godotTriplanarUv( normalMapTransform, position.xy ), window * positionDx.xy, window * positionDy.xy ) * weights.z
		+ textureGrad( normalMap, godotTriplanarUv( normalMapTransform, position.xz ), window * positionDx.xz, window * positionDy.xz ) * weights.y
		+ textureGrad( normalMap, godotTriplanarUv( normalMapTransform, position.zy * zy ), window * ( positionDx.zy * zy ), window * ( positionDy.zy * zy ) ) * weights.x;
}
#undef GODOT_NORMAL_TEXEL
#define GODOT_NORMAL_TEXEL( step ) godotTriplanarNormalTexel( vGodotUv1TriplanarPos + ( step ).x * godotUv1PositionDx + ( step ).y * godotUv1PositionDy, vGodotUv1PowerNormal + ( step ).x * godotUv1WeightsDx + ( step ).y * godotUv1WeightsDy, godotUv1PositionDx, godotUv1PositionDy )
#endif
`;
/**
 * The varyings, and a map's coordinate in a projection: the position through the texture's window,
 * as three's vertex program carries a UV through it.
 */
const FRAGMENT_PARS = `${UV_SETS.map(projectionVaryingPars).join('')}${FRAME_VARYINGS}
vec2 godotTriplanarUv( const in mat3 window, const in vec2 position ) {
	return ( window * vec3( position, 1.0 ) ).xy;
}
`;
const MAIN = 'void main() {';
/** UV1's screen gradients, before any branch, for the normal map's neighbouring texels. */
const MAIN_GRADIENTS = /* glsl */ `${MAIN}
	#if defined( GODOT_UV1_TRIPLANAR ) && defined( USE_NORMALMAP_TANGENTSPACE )
		vec3 godotUv1PositionDx = dFdx( vGodotUv1TriplanarPos );
		vec3 godotUv1PositionDy = dFdy( vGodotUv1TriplanarPos );
		vec3 godotUv1WeightsDx = dFdx( vGodotUv1PowerNormal );
		vec3 godotUv1WeightsDy = dFdy( vGodotUv1PowerNormal );
	#endif`;
const NORMAL_BEGIN = '#include <normal_fragment_begin>';
/**
 * Godot's frame in place of three's, after three's face flip: Godot flips the normal of a back face
 * and keeps its tangent and binormal (`scene_forward_clustered.glsl:1254-1265`).
 */
const TRIPLANAR_FRAME = `${NORMAL_BEGIN}
#if defined( USE_NORMALMAP_TANGENTSPACE ) || defined( USE_ANISOTROPY )
	tbn = mat3( vGodotTriplanarTangent, vGodotTriplanarBinormal, normal );
#endif`;
/**
 * `triplanar_texture` (`material.cpp:1496-1500`) for `map` in UV set `uv`. Each projection names the
 * map's sampler, so the mipmap bias finds every sample.
 */
function triplanarSample(map, uv) {
    const { position, weights } = projectionVaryings(uv);
    const sample = (coordinate) => `texture2D( ${map}, godotTriplanarUv( ${map}Transform, ${coordinate} ) )`;
    return `( ${sample(`${position}.xy`)} * ${weights}.z + ${sample(`${position}.xz`)} * ${weights}.y + ${sample(`${position}.zy * vec2( - 1.0, 1.0 )`)} * ${weights}.x )`;
}
/**
 * The UV set of each map the sample rewrite projects. The normal map reads through
 * `GODOT_NORMAL_TEXEL` instead, as the roughness limiter reads its neighbours.
 */
function projectedMaps(record) {
    return new Map(UV_SETS.flatMap((uv) => record[`${uv}Maps`].filter((map) => map !== 'normalMap').map((map) => [map, uv])));
}
/** The program text with each sample of `maps` in its UV set's projections. */
function triplanarSamples(maps) {
    if (maps.size === 0)
        return (text) => text;
    const pattern = new RegExp(String.raw `texture2D\(\s*(${[...maps.keys()].join('|')})\s*,\s*(v\w+Uv)\s*\)`, 'g');
    return (text) => rewrittenShader(text, pattern, (sample, name) => {
        const map = name;
        return sample.includes(coordinateOf(map)) ? triplanarSample(map, maps.get(map)) : sample;
    });
}
/** The window uniform of each map in `maps`, which the fragment program samples. */
function windowUniforms(maps) {
    return [...maps.keys()].map((map) => `uniform mat3 ${map}Transform;\n`).join('');
}
/**
 * The defines of one program state, in both stages. Godot writes the frame whenever a UV set
 * projects (`material.cpp:1386`), so `GODOT_TRIPLANAR_FRAME` is always on.
 */
function definesOf(record) {
    const defines = ['GODOT_TRIPLANAR_FRAME'];
    for (const uv of UV_SETS) {
        const projection = record[uv];
        if (!projection)
            continue;
        defines.push(projects(uv));
        if (projection.isWorld)
            defines.push(projectsWorld(uv));
    }
    if (record.isTangentWorld)
        defines.push('GODOT_TRIPLANAR_TANGENT_WORLD');
    if (record.isBillboard)
        defines.push('GODOT_TRIPLANAR_BILLBOARD');
    return defines;
}
/** The injection of one program state of a record. */
function triplanarProgram(record, cacheKey) {
    const defines = definesOf(record)
        .map((define) => `#define ${define}\n`)
        .join('');
    const maps = projectedMaps(record);
    const samples = triplanarSamples(maps);
    const windows = windowUniforms(maps);
    return {
        cacheKey,
        onBeforeCompile(shader) {
            Object.assign(shader.uniforms, nodePoseUniforms(this), projectionUniforms(this, 'uv1'), projectionUniforms(this, 'uv2'));
            editedProgram(shader, {
                vertex: [{ three: PROJECT_VERTEX, godot: VERTEX }],
                fragment: [{ three: MAIN, godot: MAIN_GRADIENTS }, ...litEdits(shader.fragmentShader)],
            }, 'triplanar mapping');
            shader.vertexShader = `${defines}${VERTEX_PARS}${shader.vertexShader}`;
            shader.fragmentShader = `${defines}${FRAGMENT_PARS}${windows}${rewrittenThroughChunks(shader.fragmentShader, samples)}`;
        },
    };
}
/** The edits that only a lit program takes: none for a depth program or an unshaded one. */
const LIT_EDITS = [
    { three: NORMAL_MAP_PARS, godot: TRIPLANAR_NORMAL_TEXEL },
    { three: NORMAL_BEGIN, godot: TRIPLANAR_FRAME },
];
/** The edits of `LIT_EDITS` whose anchor `fragmentShader` holds. */
function litEdits(fragmentShader) {
    return LIT_EDITS.filter((edit) => fragmentShader.includes(edit.three));
}
/** The program state a record compiles to: what changes the program, not the values its uniforms read. */
function programKey(record) {
    return [
        'godot-triplanar',
        ...definesOf(record),
        `uv1:${record.uv1Maps.join(',')}`,
        `uv2:${record.uv2Maps.join(',')}`,
    ]
        .join(' ')
        .toLowerCase();
}
/**
 * One injection per program state, so a re-parse that changes only a value keeps the program.
 * Written only by `programOf`, and never cleared: the defines and map lists bound its size.
 */
const programs = new Map();
/**
 * The injection of `record`'s program state, by the record's identity, since `syncSurfaceDepth`
 * asks on every shadow draw and a key is a string to build.
 */
const programOf = perObject((record) => {
    const key = programKey(record);
    let program = programs.get(key);
    if (!program) {
        program = triplanarProgram(record, key);
        programs.set(key, program);
    }
    return program;
});
/**
 * What a material whose `userData` records a triplanar mapping compiles with, colour and depth
 * programs alike. Undefined for one that records none.
 */
export function triplanarInjection(state) {
    const userData = state.userData;
    const record = userData?.[TRIPLANAR_KEY];
    return record ? programOf(record) : undefined;
}
//# sourceMappingURL=godotTriplanar.js.map